import React, { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAllCitiesGrouped } from "~/lib/functions/cities";
import type { RegionWithCountriesOutputGroup } from "~/lib/server/cities";
import { feature } from "topojson-client";
import type { Feature, FeatureCollection } from "geojson";
import type { Topology } from "topojson-specification";
import { getDBWithInit } from "~/lib/db";
import { cities, coaches, profiles } from "~/../drizzle/schema";
import { and, eq, isNotNull, sql } from "drizzle-orm";
import { brand } from "~/lib/ui";
import Headline from "../../../../components/coaches-city-headline";
import { CityDirectory } from "~/components/city-directory";
import { createServerFn } from "@tanstack/react-start";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export const getCities = createServerFn({ method: "GET" }).handler(async () => {
  const db = await getDBWithInit();
  const rows = await db
    .select({
      slug: cities.slug,
      label: cities.label,
      country: cities.country,
      latitude: cities.latitude,
      longitude: cities.longitude,
      coachCount: sql<number>`count(${profiles.id})`,
    })
    .from(profiles)
    .innerJoin(coaches, eq(profiles.coachId, coaches.id))
    .innerJoin(cities, eq(profiles.closestMajorCity, cities.slug))
    .where(
      and(
        isNotNull(profiles.closestMajorCity),
        isNotNull(profiles.primaryCountry),
        eq(profiles.isPublic, true),
      ),
    )
    .groupBy(
      cities.slug,
      cities.label,
      cities.country,
      cities.latitude,
      cities.longitude,
    )
    .having(sql`count(${profiles.id}) >= 2`);
  return rows.map((r) => ({
    value: r.slug,
    label: r.label,
    coordinates: [r.longitude!, r.latitude!] as [number, number],
  }));
});

export const getCityOptions = createServerFn({ method: "GET" }).handler(
  async () => {
    return await getAllCitiesGrouped();
  },
);

export const getWorld = createServerFn({ method: "GET" }).handler(
  async (): Promise<FeatureCollection> => {
    const res = await fetch(geoUrl);
    if (!res.ok) {
      throw new Error("Failed to load map data");
    }
    const topo: Topology = await res.json();
    const geojson = feature(topo, topo.objects.countries);
    return geojson as unknown as FeatureCollection;
  },
);

function CoachesCityIndexPage() {
  const { data: cities = [] } = useQuery({
    queryKey: ["cities"],
    queryFn: () => getCities(),
  });
  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cityOptions"],
    queryFn: () => getCityOptions(),
  });
  const { data: world } = useQuery({
    queryKey: ["world"],
    queryFn: () => getWorld(),
  });

  const [isMobile, setIsMobile] = useState(false);

  // track mobile viewport to limit labels
  useEffect(() => {
    setIsMobile(640 > window.innerWidth);
    const onResize = () => setIsMobile(640 > window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const width = 1000;
  const height = 600;
  const [d3Geo, setD3Geo] = useState<{
    geoEqualEarth: any;
    geoPath: any;
  } | null>(null);

  useEffect(() => {
    const loadD3 = async () => {
      const { geoEqualEarth, geoPath } = await import("d3-geo");
      setD3Geo({ geoEqualEarth, geoPath });
    };
    loadD3();
  }, []);

  const projection =
    world && d3Geo
      ? d3Geo.geoEqualEarth().fitSize([width, height], world)
      : null;
  const pathGen =
    projection && d3Geo ? d3Geo.geoPath().projection(projection) : null;

  // Setup pan & zoom
  const [svgRef, setSvgRef] = useState<SVGSVGElement | null>(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });

  // clamp function to enforce zoom and pan bounds
  const clampTransform = (currentTransform: typeof transform) => {
    const minZoom = 0.5;
    const maxZoom = 8;
    const clampedK = Math.min(Math.max(currentTransform.k, minZoom), maxZoom);
    const VBoxWidth = width;
    const VBoxHeight = height;
    const minX = VBoxWidth * (1 - clampedK) - (VBoxWidth * (clampedK - 1)) / 2;
    const maxX = (VBoxWidth * (clampedK - 1)) / 2;
    const minY =
      VBoxHeight * (1 - clampedK) - (VBoxHeight * (clampedK - 1)) / 2;
    const maxY = (VBoxHeight * (clampedK - 1)) / 2;
    const clampedX = Math.min(Math.max(currentTransform.x, minX), maxX);
    const clampedY = Math.min(Math.max(currentTransform.y, minY), maxY);
    return { x: clampedX, y: clampedY, k: clampedK };
  };

  // helper to zoom around map center
  const zoomBy = (factor: number) => {
    setTransform((prev) => {
      const prevK = prev.k;
      const newK = prevK * factor;
      const centerX = width / 2;
      const centerY = height / 2;
      const newX = centerX - ((centerX - prev.x) * newK) / prevK;
      const newY = centerY - ((centerY - prev.y) * newK) / prevK;
      return clampTransform({ x: newX, y: newY, k: newK });
    });
  };
  const zoomIn = () => zoomBy(1.2);
  const zoomOut = () => zoomBy(1 / 1.2);

  // Frontend-only zoom & pan with cursor-centered zoom and bounds
  useEffect(() => {
    const svg = svgRef;
    if (!svg) {
      return;
    }

    let isDragging = false;
    let startX = 0,
      startY = 0;

    // Mouse Pan
    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      svg.style.cursor = "grabbing";
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      setTransform((prev) => {
        const newTransform = { ...prev, x: prev.x + dx, y: prev.y + dy };
        return clampTransform(newTransform);
      });
      startX = e.clientX;
      startY = e.clientY;
    };
    const onMouseUp = () => {
      isDragging = false;
      svg.style.cursor = "grab";
    };
    const onMouseLeave = () => {
      if (isDragging) {
        isDragging = false;
        svg.style.cursor = "grab";
      }
    };

    // Mouse Wheel Zoom
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) {
        return;
      } // only zoom when holding ctrl
      e.preventDefault();
      const scaleAmount = -e.deltaY * 0.002;
      const rect = svg.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      setTransform((prev) => {
        const { x: prevX, y: prevY, k: prevK } = prev;
        const newK = prevK * (1 + scaleAmount);
        const newX = mouseX - ((mouseX - prevX) * newK) / prevK;
        const newY = mouseY - ((mouseY - prevY) * newK) / prevK;
        return clampTransform({ x: newX, y: newY, k: newK });
      });
    };

    svg.addEventListener("wheel", onWheel, { passive: false });
    svg.addEventListener("mousedown", onMouseDown);
    svg.addEventListener("mousemove", onMouseMove);
    svg.addEventListener("mouseup", onMouseUp);
    svg.addEventListener("mouseleave", onMouseLeave);

    // Touch interactions (Pan and Pinch-to-Zoom)
    let initialPinchDistance = 0;
    let isPinching = false;
    let lastTouchX = 0,
      lastTouchY = 0; // For single-finger pan

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getMidpoint = (touches: TouchList) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (1 === e.touches.length) {
        // Start single-finger pan
        isDragging = true;
        lastTouchX = e.touches[0].clientX;
        lastTouchY = e.touches[0].clientY;
        svg.style.cursor = "grabbing";
      } else if (2 === e.touches.length) {
        // Start pinch-zoom
        isDragging = false; // Stop pan if it was active
        isPinching = true;
        initialPinchDistance = getDistance(e.touches);
        // Store initial midpoint for centering zoom, relative to SVG content
        const rect = svg.getBoundingClientRect();
        const midpoint = getMidpoint(e.touches);
        startX = (midpoint.x - rect.left - transform.x) / transform.k;
        startY = (midpoint.y - rect.top - transform.y) / transform.k;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (isDragging && 1 === e.touches.length) {
        // Single-finger pan
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const dx = touchX - lastTouchX;
        const dy = touchY - lastTouchY;
        setTransform((prev) => {
          const newTransform = { ...prev, x: prev.x + dx, y: prev.y + dy };
          return clampTransform(newTransform);
        });
        lastTouchX = touchX;
        lastTouchY = touchY;
      } else if (isPinching && 2 === e.touches.length) {
        // Pinch-zoom
        const newDistance = getDistance(e.touches);
        const scaleFactor = newDistance / initialPinchDistance;

        const rect = svg.getBoundingClientRect();
        const mid = getMidpoint(e.touches);
        const clientX = mid.x - rect.left;
        const clientY = mid.y - rect.top;

        setTransform((prev) => {
          const { k: prevK } = prev;
          const newK = prevK * scaleFactor;
          const newX = clientX - (clientX - prev.x) * (newK / prevK);
          const newY = clientY - (clientY - prev.y) * (newK / prevK);
          return clampTransform({ x: newX, y: newY, k: newK });
        });

        initialPinchDistance = newDistance; // Update for continuous pinch
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (isDragging && 0 === e.touches.length) {
        isDragging = false;
        svg.style.cursor = "grab";
      }
      if (isPinching && 2 > e.touches.length) {
        isPinching = false;
      }
    };

    svg.addEventListener("touchstart", onTouchStart, { passive: false });
    svg.addEventListener("touchmove", onTouchMove, { passive: false });
    svg.addEventListener("touchend", onTouchEnd);
    svg.addEventListener("touchcancel", onTouchEnd); // Handle cancellations too

    return () => {
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("mousedown", onMouseDown);
      svg.removeEventListener("mousemove", onMouseMove);
      svg.removeEventListener("mouseup", onMouseUp);
      svg.removeEventListener("mouseleave", onMouseLeave);
      svg.removeEventListener("touchstart", onTouchStart);
      svg.removeEventListener("touchmove", onTouchMove);
      svg.removeEventListener("touchend", onTouchEnd);
      svg.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [svgRef, transform]);

  if (!world || !projection || !pathGen) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex min-h-[80vh] w-full flex-col items-center justify-center bg-white py-12">
      <Headline />
      <div className="group relative mx-auto w-full px-2 sm:px-4 lg:px-6">
        <div className="relative h-[75vh] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:aspect-[16/7] sm:h-auto">
          {/* overlay zoom controls */}
          <div className="absolute right-2 top-2 z-10 flex flex-col space-y-2">
            <button
              onClick={zoomIn}
              className="flex items-center justify-center rounded-full bg-[var(--brand-500)] p-2 shadow-lg hover:bg-[var(--brand-600)]"
              aria-label="Zoom in"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 5a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H6a1 1 0 110-2h5V6a1 1 0 011-1z" />
              </svg>
            </button>
            <button
              onClick={zoomOut}
              className="flex items-center justify-center rounded-full bg-[var(--brand-500)] p-2 shadow-lg hover:bg-[var(--brand-600)]"
              aria-label="Zoom out"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M5 11h14a1 1 0 010 2H5a1 1 0 010-2z" />
              </svg>
            </button>
          </div>
          <svg
            ref={setSvgRef}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            className="h-full w-full cursor-default"
            style={{ cursor: "grab", touchAction: "none" }}
          >
            {/* Only map paths are scaled */}
            <g
              style={{
                transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
              }}
            >
              {/* Country paths */}
              {world.features.map((feat: Feature, idx: number) => (
                <path
                  key={idx}
                  d={pathGen(feat)!}
                  fill="#f3f4f6"
                  stroke="#e5e7eb"
                  strokeWidth="0.5"
                />
              ))}
            </g>

            {/* Marker circles: constant size, manually positioned */}
            <g id="marker-circles">
              {cities.map((m) => {
                const [wx, wy] = projection(m.coordinates)!;
                const x = wx * transform.k + transform.x;
                const y = wy * transform.k + transform.y;
                return (
                  <a
                    key={m.value}
                    href={`/coaches/city/${m.value}`}
                    className="cursor-pointer"
                    tabIndex={0}
                    aria-label={`View coaches in ${m.label}`}
                  >
                    <circle
                      cx={x}
                      cy={y}
                      r={isMobile ? 12 : 6}
                      fill={brand["200"]}
                      stroke={brand["400"]}
                      strokeWidth={1.5}
                      className="transition-colors hover:fill-[var(--brand-400)] hover:stroke-[var(--brand-500)]"
                    />
                  </a>
                );
              })}
            </g>

            {/* Marker labels: constant font, manually positioned */}
            <g id="marker-labels">
              {cities.map((m) => {
                const [wx, wy] = projection(m.coordinates)!;
                const x = wx * transform.k + transform.x;
                const y = wy * transform.k + transform.y - 14;
                return (
                  <a
                    key={m.value}
                    href={`/coaches/city/${m.value}`}
                    className="group cursor-pointer"
                    tabIndex={0}
                    aria-label={`View coaches in ${m.label}`}
                  >
                    <text
                      x={x}
                      y={y}
                      textAnchor="middle"
                      fontSize={isMobile ? "24" : "12"}
                      fontWeight="bold"
                      fill={brand["900"]}
                      stroke="#fff"
                      strokeWidth={isMobile ? "2" : "3"}
                      paintOrder="stroke"
                      className="pointer-events-none select-none"
                      style={{
                        strokeLinejoin: "round",
                      }}
                    >
                      {m.label}
                    </text>
                  </a>
                );
              })}
            </g>
          </svg>
        </div>
      </div>
      <div className="mb-16 mt-12 w-full max-w-4xl px-4">
        <CityDirectory
          cityData={cityOptions as RegionWithCountriesOutputGroup[]}
          defaultOpenRegions={["North America", "Europe"]}
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_public/coaches/city/")({
  head: () => ({
    title: "Coaches by City - Map View",
    meta: [
      {
        name: "description",
        content: "Interactive map of coach locations by city.",
      },
    ],
  }),
  loader: async ({ context }) => {
    // Pre-load data into React Query
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["cities"],
        queryFn: () => getCities(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityOptions"],
        queryFn: () => getCityOptions(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["world"],
        queryFn: () => getWorld(),
      }),
    ]);
    return {};
  },
  component: CoachesCityIndexPage,
});
